import { useCallback, useMemo, useRef } from 'react';

import type { ClaimWorkItem } from '../../../store';
import { billingApi, billingEncountersApi } from '../../../store/api';
import { billingV2Api } from '../../../store/v2-api';

/**
 * Hover prefetch for worklist rows (GDN-3201): warm the workbench's first
 * fetch wave — the claim-workspace GET (~2.35s at the production median,
 * GDN-3199), the claim record, and the certification — while the biller's
 * cursor is still deciding. RTK Query's own prefetch machinery does the
 * work, so this never invents cache entries: a real request warms the real
 * cache, or nothing happens.
 *
 * Guard rails, each one load-bearing:
 * - TTL-respecting: `ifOlderThan` makes prefetch a no-op while the cache
 *   already holds data fresher than the window — hovering never spams a
 *   warm cache.
 * - Once per row per mount: one biller sweep across the list must not
 *   re-fire on every re-entry; the TTL alone can't stop the burst of
 *   enter/leave/enter on the same row.
 * - Hover-capable pointers only: on touch devices the "hover" is the tap
 *   itself — prefetching there would fire alongside the navigation it was
 *   meant to precede, doubling requests for nothing.
 *
 * The active-batch skip lives in the CALLER (ClaimsTab), which owns the
 * batch state; this hook stays ignorant of batches by design.
 */

/** Don't re-request data fresher than this. Matches the intent of a hover:
 *  warm a COLD cache for an imminent click, not keep a hot one hot. */
export const WORKLIST_PREFETCH_TTL_SECONDS = 30;

export interface WorklistRowPrefetch {
  prefetchRow: (item: ClaimWorkItem) => void;
  /** Whether a prefetch was ATTEMPTED for this row this mount — the
   *  `prefetched` flag on billing_claim_worklist_row_clicked, the
   *  optimization's own meter (hit rate, not just adoption). */
  wasPrefetched: (claimId: string) => boolean;
}

export function useWorklistRowPrefetch(): WorklistRowPrefetch {
  const prefetchWorkspace = billingEncountersApi.usePrefetch(
    'getV2ProviderBillingEncountersByEncounterIdClaimWorkspace',
    { ifOlderThan: WORKLIST_PREFETCH_TTL_SECONDS },
  );
  const prefetchClaim = billingApi.usePrefetch('getV2ProviderClaimsByClaimId', {
    ifOlderThan: WORKLIST_PREFETCH_TTL_SECONDS,
  });
  const prefetchCertification = billingV2Api.usePrefetch(
    'getV2ProviderClaimsByClaimIdCertification',
    { ifOlderThan: WORKLIST_PREFETCH_TTL_SECONDS },
  );

  /* Evaluated once per mount: pointer capability doesn't change mid-session,
     and a matchMedia call per hover would be waste. `?? false` covers jsdom
     and any environment without matchMedia — no hover, no prefetch. */
  const canHover = useMemo(
    () => window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false,
    [],
  );
  const seenRef = useRef(new Set<string>());

  const prefetchRow = useCallback(
    (item: ClaimWorkItem) => {
      if (!canHover) return;
      if (seenRef.current.has(item.claim_id)) return;
      seenRef.current.add(item.claim_id);

      /* Mirrors the workbench's own mount wave (GDN-3199): claim and
         certification key on claim_id; the workspace GET needs the direct
         route's encounter_id — gate-fallback rows warm what they can.
         The workspace arg carries BOTH ids because the workbench subscribes
         with both (useClaimWorkspaceModel) and RTK Query's cache key is the
         serialized arg — an encounterId-only prefetch would warm an entry
         the workbench never reads. */
      prefetchClaim({ claimId: item.claim_id });
      prefetchCertification({ claimId: item.claim_id });
      if (item.encounter_id) {
        prefetchWorkspace({ claimId: item.claim_id, encounterId: item.encounter_id });
      }
    },
    [canHover, prefetchCertification, prefetchClaim, prefetchWorkspace],
  );

  const wasPrefetched = useCallback((claimId: string) => seenRef.current.has(claimId), []);

  return useMemo(() => ({ prefetchRow, wasPrefetched }), [prefetchRow, wasPrefetched]);
}
